<script setup lang="ts">
/**
 * Modal centré affiché pour les erreurs RÉCUPÉRABLES du système de paiement
 * (network, 429, 5xx). Téléporté au <body> pour échapper aux stacking
 * contexts des iframes Stripe / PayPal.
 *
 * Extrait de `pages/c/[slug]/payment.vue` lors du split anti-800l.
 */
defineProps<{
  show: boolean;
  errorMessage: string;
  retrying: boolean;
}>();

const emit = defineEmits<{
  dismiss: [];
  retry: [];
}>();

function onDismiss() {
  emit("dismiss");
}

function onRetry() {
  emit("retry");
}

const { t } = useI18n();
</script>

<template>
  <Teleport to="body">
    <Transition
      enter-active-class="transition-opacity duration-200"
      enter-from-class="opacity-0"
      enter-to-class="opacity-100"
      leave-active-class="transition-opacity duration-150"
      leave-from-class="opacity-100"
      leave-to-class="opacity-0"
    >
      <div
        v-if="show"
        class="fixed inset-0 z-9999 flex items-center justify-center px-5 py-10"
        role="dialog"
        aria-modal="true"
        aria-labelledby="payment-error-title"
      >
        <!-- Backdrop -->
        <div
          class="absolute inset-0 bg-black/40 backdrop-blur-sm"
          @click="onDismiss"
        />

        <!-- Carte -->
        <Transition
          enter-active-class="transition-all duration-250 ease-out"
          enter-from-class="opacity-0 scale-95"
          enter-to-class="opacity-100 scale-100"
          leave-active-class="transition-all duration-150 ease-in"
          leave-from-class="opacity-100 scale-100"
          leave-to-class="opacity-0 scale-95"
        >
          <div
            v-if="show"
            class="relative w-full max-w-sm bg-white dark:bg-[#1C1C1E] rounded-[20px] shadow-[0_4px_24px_rgba(0,0,0,0.16)] dark:shadow-[0_4px_24px_rgba(0,0,0,0.5)] overflow-hidden"
          >
            <!-- Close button -->
            <button
              type="button"
              class="absolute top-3 right-3 p-2 rounded-full text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-[#2C2C2E] transition-colors z-10"
              :aria-label="t('commun.actions.fermer')"
              @click="onDismiss"
            >
              <UIcon name="i-lucide-x" class="size-4" />
            </button>

            <!-- Content -->
            <div class="px-6 py-10 text-center">
              <!-- Icône -->
              <div class="mb-6">
                <div class="w-20 h-20 mx-auto rounded-full flex items-center justify-center bg-amber-50 dark:bg-amber-950/30">
                  <UIcon name="i-lucide-wifi-off" class="size-10 text-amber-500" />
                </div>
              </div>

              <!-- Title -->
              <h1
                id="payment-error-title"
                class="text-[22px] font-semibold text-[#1C1C1E] dark:text-white mb-2 tracking-tight"
              >
                {{ t('campagne.modalErreurRecuperable.titre') }}
              </h1>

              <!-- Message -->
              <p class="text-[15px] text-[#8E8E93] dark:text-[#98989D] leading-relaxed max-w-[280px] mx-auto">
                {{ errorMessage || t('campagne.modalErreurRecuperable.messageDefaut') }}
              </p>
            </div>

            <!-- Action button -->
            <div class="px-6 pb-6 pt-2">
              <button
                type="button"
                :disabled="retrying"
                class="w-full py-3.5 rounded-xl text-base font-semibold text-white bg-[#212121] hover:bg-[#333] active:bg-black transition-colors shadow-sm hover:shadow-md disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-[#212121]/40 dark:bg-white dark:text-[#212121] dark:hover:bg-gray-100 dark:active:bg-gray-200 inline-flex items-center justify-center gap-2"
                @click="onRetry"
              >
                <UIcon
                  v-if="retrying"
                  name="i-lucide-loader-2"
                  class="size-4 animate-spin"
                />
                <UIcon v-else name="i-lucide-rotate-cw" class="size-4" />
                {{ retrying ? t('campagne.modalErreurRecuperable.tentativeEnCours') : t('commun.actions.reessayer') }}
              </button>
            </div>
          </div>
        </Transition>
      </div>
    </Transition>
  </Teleport>
</template>
